/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 23/12/21
 */

import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import {
    useStatefulRequest,
    UseStatefulRequestOptions,
    UseStatefulRequestOutput,
} from '@/utils/useStatefulRequests/useStatefulRequest';

export type UseStatefulRequestWithoutDataOutput<ResponseData> = Omit<
    UseStatefulRequestOutput<ResponseData>,
    'makeRequest'
> & {
    makeRequest: (url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<ResponseData>>;
};

export const useStatefulRequestWithoutData = <ResponseData = unknown>(
    method: Method,
    options?: UseStatefulRequestOptions,
): UseStatefulRequestWithoutDataOutput<ResponseData> => {
    const {
        makeRequest: makeBaseRequest,
        responseData,
        loading,
        error,
        cancel,
        clear,
    } = useStatefulRequest<ResponseData>(options);

    const makeRequestWithoutData = async (url: string, config?: AxiosRequestConfig) => {
        return await makeBaseRequest({
            ...config,
            method,
            url,
        });
    };

    return {
        makeRequest: makeRequestWithoutData,
        responseData,
        loading,
        error,
        cancel,
        clear,
    };
};
